import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, CalendarDays, CheckCircle2, Loader2, Mail, MapPin, Phone, Save,
} from "lucide-react";
import { usePortalAuth, formatApiErrorDetail } from "@/components/portal/PortalAuth";
import {
  Card, Spinner, SIMPLE_STATUSES, StatusBadge, btnPrimary, fmtDate, fmtDay, gbp2, inputCls,
  leadStatusFor, simpleStatus,
} from "@/components/portal/shared";
import { MeasurementSheet } from "@/components/portal/MeasurementSheet";
import { QuoteBuilder } from "@/components/portal/QuoteBuilder";
import { PhotoUpload } from "@/components/portal/PhotoUpload";
import { RecordPaymentModal, ScheduleInstallModal } from "@/components/portal/QuickActions";

const TABS = [
  ["overview", "Overview"], ["notes", "Notes"], ["visits", "Home Visits"],
  ["quotes", "Quotes"], ["orders", "Orders"], ["installations", "Installations"],
  ["payments", "Payments"], ["documents", "Documents"], ["activity", "Activity Timeline"],
];

const Field = ({ label, children }) => (
  <div><dt className="text-xs text-slate-400">{label}</dt><dd className="font-medium text-slate-800 text-sm">{children || "—"}</dd></div>
);

export default function CustomerRecordPage() {
  const { id } = useParams();
  const { api } = usePortalAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [lead, setLead] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [team, setTeam] = useState([]);
  const [note, setNote] = useState("");
  const [visitDate, setVisitDate] = useState("");
  const [visitTime, setVisitTime] = useState("");
  const [payOrder, setPayOrder] = useState(null);
  const [schedOrder, setSchedOrder] = useState(null);
  const [hsBusy, setHsBusy] = useState(false);
  const [error, setError] = useState("");

  const pushToHubSpot = async () => {
    if (hsBusy) return;
    setHsBusy(true);
    try {
      await api.post(`/leads/${id}/hubspot-sync`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setHsBusy(false);
    }
  };

  const load = async () => {
    const [l, q, o] = await Promise.all([
      api.get(`/leads/${id}`),
      api.get("/quotes", { params: { lead_id: id } }),
      api.get("/orders"),
    ]);
    setLead(l.data);
    setQuotes(q.data.quotes);
    setOrders(o.data.orders.filter((x) => x.lead_id === id));
    setVisitDate(l.data.appointment?.date || "");
    setVisitTime(l.data.appointment?.time || "");
  };

  useEffect(() => {
    load().catch(() => setLead(false));
    api.get("/team").then((r) => setTeam(r.data.users)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const activity = useMemo(() => {
    if (!lead) return [];
    const items = [
      { at: lead.created_at, text: `Enquiry received via ${lead.source || "website"}` },
      ...(lead.enquiries || []).map((e) => ({ at: e.submitted_at, text: `Enquiry: ${e.interest || "Message"}${e.page ? ` (${e.page})` : ""}` })),
      ...(lead.notes || []).map((n) => ({ at: n.at, text: `Note: ${n.text}${n.by ? ` — ${n.by}` : ""}` })),
      ...quotes.map((q) => ({ at: q.created_at, text: `Quote ${q.ref} created (${gbp2(q.total)})` })),
      ...quotes.filter((q) => q.sent_at).map((q) => ({ at: q.sent_at, text: `Quote ${q.ref} emailed to customer` })),
      ...quotes.filter((q) => q.accepted_at).map((q) => ({ at: q.accepted_at, text: `Quote ${q.ref} accepted by customer` })),
      ...orders.map((o) => ({ at: o.created_at, text: `Order ${o.ref} created (${gbp2(o.total)})` })),
      ...orders.filter((o) => o.deposit_date).map((o) => ({ at: o.deposit_date, text: `Deposit ${gbp2(o.deposit_paid)} recorded on ${o.ref}` })),
      ...orders.filter((o) => o.balance_date).map((o) => ({ at: o.balance_date, text: `Balance ${gbp2(o.balance_paid)} recorded on ${o.ref}` })),
      ...orders.filter((o) => o.installed_at).map((o) => ({ at: o.installed_at, text: `${o.ref} installed — signed by ${o.completion_name || "customer"}` })),
    ];
    return items.filter((i) => i.at).sort((a, b) => String(b.at).localeCompare(String(a.at)));
  }, [lead, quotes, orders]);

  if (lead === null) return <Spinner />;
  if (lead === false) return <p className="text-sm text-red-700" role="alert">Customer record not found.</p>

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, body);
      setLead(data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const patchOrder = async (orderId, body) => {
    await api.patch(`/orders/${orderId}`, body);
    await load();
  };

  const name = `${lead.firstName || ""} ${lead.lastName || ""}`.trim();
  const address = [lead.address1, lead.address2, lead.town, lead.postcode].filter(Boolean).join(", ");

  return (
    <div data-testid="customer-record" className="space-y-5">
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-medium text-navy-900 hover:text-navy-800" data-testid="customer-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <Card>
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0">
            <h1 className="font-serif text-3xl text-navy-900" data-testid="customer-name">{name}</h1>
            <p className="text-xs text-slate-500 mt-1">{lead.cust_ref} · {lead.source} · since {fmtDay(lead.created_at)}</p>
          </div>
          <span className="mt-1.5"><StatusBadge s={simpleStatus(lead.status)} simple /></span>
          <div className="ml-auto flex flex-wrap gap-2">
            {lead.phone && <a href={`tel:${lead.phone}`} data-testid="customer-call" className={btnPrimary.replace("bg-navy-900 text-white hover:bg-navy-800", "border border-warm-300 text-slate-700 hover:border-navy-800/40 bg-white")}><Phone className="h-3.5 w-3.5" /> Call</a>}
            {lead.email && <a href={`mailto:${lead.email}`} data-testid="customer-email" className={btnPrimary.replace("bg-navy-900 text-white hover:bg-navy-800", "border border-warm-300 text-slate-700 hover:border-navy-800/40 bg-white")}><Mail className="h-3.5 w-3.5" /> Email</a>}
            {address && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`} target="_blank" rel="noopener noreferrer" data-testid="customer-map" className={btnPrimary.replace("bg-navy-900 text-white hover:bg-navy-800", "border border-warm-300 text-slate-700 hover:border-navy-800/40 bg-white")}>
                <MapPin className="h-3.5 w-3.5" /> Map
              </a>
            )}
            {lead.hubspot?.deal_id ? (
              <span
                data-testid="customer-hubspot-synced"
                title={`HubSpot contact ${lead.hubspot.contact_id} · deal ${lead.hubspot.deal_id}${lead.hubspot.synced_stage ? ` · stage: ${lead.hubspot.synced_stage}` : ""}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-800"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> HubSpot synced
              </span>
            ) : (
              <button
                type="button"
                data-testid="customer-hubspot-push"
                onClick={pushToHubSpot}
                disabled={hsBusy}
                className={`${btnPrimary.replace("bg-navy-900 text-white hover:bg-navy-800", lead.hubspot?.status === "failed" ? "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100" : "border border-warm-300 text-slate-700 hover:border-navy-800/40 bg-white")}`}
              >
                {hsBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {lead.hubspot?.status === "failed" ? "HubSpot sync failed — Retry" : "Push to HubSpot"}
              </button>
            )}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5" htmlFor="cr-status">Status</label>
            <select id="cr-status" data-testid="customer-status-select" value={simpleStatus(lead.status)} onChange={(e) => patch({ status: leadStatusFor(e.target.value) })} className={inputCls}>
              {SIMPLE_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5" htmlFor="cr-assigned">Assigned to</label>
            <select id="cr-assigned" data-testid="customer-assigned-select" value={lead.assigned_to || ""} onChange={(e) => patch({ assigned_to: e.target.value })} className={inputCls}>
              <option value="">Unassigned</option>
              {team.map((u) => <option key={u.id} value={u.name}>{u.name} ({u.role})</option>)}
            </select>
          </div>
        </div>
        {error && <p role="alert" className="text-xs text-red-700 mt-3">{error}</p>}
      </Card>

      <div className="flex gap-1.5 overflow-x-auto pb-1 border-b border-warm-200" role="tablist" aria-label="Customer record sections">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            data-testid={`customer-tab-${key}`}
            onClick={() => setParams({ tab: key })}
            className={`whitespace-nowrap rounded-t-lg px-4 py-2.5 text-xs font-semibold transition-colors border-b-2 ${
              tab === key ? "border-navy-900 text-navy-900 bg-white" : "border-transparent text-slate-500 hover:text-navy-900"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-5" data-testid="tab-overview">
          <Card title="Contact & Address">
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-4">
              <Field label="Phone">{lead.phone}</Field>
              <Field label="Email">{lead.email}</Field>
              <Field label="Address">{address}</Field>
              <Field label="Product interest">{lead.interest}</Field>
              <Field label="Rooms">{lead.rooms?.join(", ")}</Field>
              <Field label="Preferred time">{lead.preferredTime}</Field>
              <Field label="Page">{lead.page}</Field>
              <Field label="Marketing consent">{lead.consentMarketing ? "Yes" : "No"}</Field>
              <Field label="Campaign">{lead.utm && Object.keys(lead.utm).length > 0 ? Object.entries(lead.utm).map(([k, v]) => `${k.replace("utm_", "")}=${v}`).join(" · ") : "—"}</Field>
            </dl>
            {lead.message && <div className="rounded-xl bg-warm-100 p-4 text-sm text-slate-700 mt-4">“{lead.message}”</div>}
          </Card>
          <Card>
            <MeasurementSheet lead={lead} api={api} onSaved={load} />
          </Card>
        </div>
      )}

      {tab === "notes" && (
        <Card title="Notes" testid="tab-notes">
          {lead.notes?.length > 0 ? (
            <ul className="space-y-2 mb-5">
              {lead.notes.map((n, i) => (
                <li key={i} className="rounded-xl bg-warm-100 p-3.5 text-xs text-slate-700">
                  {n.text}<span className="block text-slate-400 mt-1">{fmtDate(n.at)}{n.by ? ` · ${n.by}` : ""}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-slate-400 mb-5">No notes yet.</p>}
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" aria-label="Add a note" data-testid="customer-note-input" className={`${inputCls} resize-none`} />
          <button type="button" data-testid="customer-note-save" disabled={!note.trim()} onClick={() => { patch({ note }); setNote(""); }} className={`${btnPrimary} mt-3 w-full py-3`}>Save Note</button>
        </Card>
      )}

      {tab === "visits" && (
        <Card title="Home Visit" testid="tab-visits">
          {lead.appointment?.date ? (
            <p className="text-xs text-emerald-800 bg-emerald-50 rounded-lg px-3 py-2 mb-4" data-testid="visit-current">
              Booked: {fmtDay(lead.appointment.date)}{lead.appointment.time ? ` at ${lead.appointment.time}` : ""}
            </p>
          ) : <p className="text-sm text-slate-400 mb-4">No visit booked yet.</p>}
          <div className="grid grid-cols-2 gap-3">
            <input type="date" aria-label="Visit date" data-testid="visit-date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} className={inputCls} />
            <input type="time" aria-label="Visit time" data-testid="visit-time" value={visitTime} onChange={(e) => setVisitTime(e.target.value)} className={inputCls} />
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button type="button" data-testid="visit-save" disabled={!visitDate} onClick={() => patch({ appointment: { date: visitDate, time: visitTime } })} className={btnPrimary}>
              <Save className="h-3.5 w-3.5" /> {lead.appointment?.date ? "Reschedule" : "Book Visit"}
            </button>
            {lead.appointment?.date && lead.status !== "Home Visit Completed" && (
              <button type="button" data-testid="visit-complete" onClick={() => patch({ status: "Home Visit Completed" })} className={btnPrimary}>
                <CheckCircle2 className="h-3.5 w-3.5" /> Mark Visit Complete
              </button>
            )}
          </div>
        </Card>
      )}

      {tab === "quotes" && (
        <Card testid="tab-quotes"><QuoteBuilder lead={lead} api={api} /></Card>
      )}

      {tab === "orders" && (
        <div className="space-y-4" data-testid="tab-orders">
          {orders.length === 0 ? (
            <Card><p className="text-sm text-slate-400">No orders yet — orders appear when a quote is accepted, or create one from the dashboard quick actions.</p></Card>
          ) : orders.map((o) => (
            <Card key={o.id} testid={`order-card-${o.id}`} title={o.ref} action={<StatusBadge s={o.status} />}>
              <ul className="space-y-1.5 text-xs text-slate-700 mb-4">
                {o.items.map((i, x) => <li key={x} className="flex justify-between gap-3"><span>{i.description} × {i.qty}</span><span className="font-medium">{gbp2(i.price * i.qty)}</span></li>)}
              </ul>
              <p className="flex justify-between border-t border-warm-200 pt-2.5 text-sm font-semibold"><span>Total</span><span>{gbp2(o.total)}</span></p>
              <p className="text-xs text-slate-500 mt-1">Deposit {gbp2(o.deposit_paid)} / {gbp2(o.deposit_required)} · Balance {gbp2(o.balance_paid)} / {gbp2(o.balance_due)}</p>
            </Card>
          ))}
        </div>
      )}

      {tab === "installations" && (
        <div className="space-y-4" data-testid="tab-installations">
          {orders.length === 0 ? <Card><p className="text-sm text-slate-400">No installations to schedule yet.</p></Card> : orders.map((o) => (
            <Card key={o.id} testid={`install-card-${o.id}`} title={`${o.ref} installation`} action={<StatusBadge s={o.status} />}>
              {o.install_date ? (
                <p className="text-sm text-slate-700 mb-3 flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-navy-900" />
                  {fmtDay(o.install_date)}{o.install_time ? ` at ${o.install_time}` : ""}{o.installer ? ` · ${o.installer}` : ""}
                </p>
              ) : <p className="text-sm text-slate-400 mb-3">Not scheduled.</p>}
              {o.completion_signature ? (
                <p className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-900 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" /> Completed and signed by {o.completion_name}
                </p>
              ) : (
                <button type="button" data-testid={`install-schedule-${o.id}`} onClick={() => setSchedOrder(o)} className={btnPrimary}>Schedule / Reschedule</button>
              )}
            </Card>
          ))}
        </div>
      )}

      {tab === "payments" && (
        <div className="space-y-4" data-testid="tab-payments">
          {orders.length === 0 ? <Card><p className="text-sm text-slate-400">No payments to track yet.</p></Card> : orders.map((o) => {
            const depOut = Math.max(0, (o.deposit_required || 0) - (o.deposit_paid || 0));
            const balOut = Math.max(0, (o.balance_due || 0) - (o.balance_paid || 0));
            return (
              <Card key={o.id} testid={`payment-card-${o.id}`} title={o.ref} action={depOut + balOut > 0 ? <span className="text-sm font-semibold text-amber-700">{gbp2(depOut + balOut)} due</span> : <span className="text-sm font-semibold text-emerald-700">Paid in full</span>}>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm mb-4">
                  <Field label="Order total">{gbp2(o.total)}</Field>
                  <Field label="Deposit">{gbp2(o.deposit_paid)} of {gbp2(o.deposit_required)}{o.deposit_method ? ` · ${o.deposit_method}` : ""}</Field>
                  <Field label="Balance">{gbp2(o.balance_paid)} of {gbp2(o.balance_due)}{o.balance_method ? ` · ${o.balance_method}` : ""}</Field>
                  <Field label="Status">{o.status}</Field>
                </dl>
                {depOut + balOut > 0 && (
                  <button type="button" data-testid={`payment-record-${o.id}`} onClick={() => setPayOrder(o)} className={btnPrimary}>Record Payment</button>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {tab === "documents" && (
        <Card title="Photos & Documents" testid="tab-documents">
          <PhotoUpload
            api={api}
            attachUrl={`/leads/${id}/photos`}
            photos={lead.photos || []}
            onChange={load}
            tags={["Survey", "Window", "Fabric", "Problem", "Document", "Other"]}
          />
        </Card>
      )}

      {tab === "activity" && (
        <Card title="Activity Timeline" testid="tab-activity">
          {activity.length === 0 ? <p className="text-sm text-slate-400">No activity yet.</p> : (
            <ol className="relative border-l border-warm-300 ml-1.5 space-y-4">
              {activity.map((a, i) => (
                <li key={i} className="pl-5 relative">
                  <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-navy-900" />
                  <p className="text-sm text-slate-700">{a.text}</p>
                  <p className="text-[11px] text-slate-400">{fmtDate(a.at)}</p>
                </li>
              ))}
            </ol>
          )}
        </Card>
      )}

      {payOrder && <RecordPaymentModal order={payOrder} onClose={() => setPayOrder(null)} onDone={load} />}
      {schedOrder && <ScheduleInstallModal order={schedOrder} onClose={() => setSchedOrder(null)} onDone={load} />}
    </div>
  );
}
